/**
 * Dates the way the inbox says them: «hoy 23:07», «ayer 16:31», «11 sept».
 *
 * A column of «10/9/26, 10:15» is a column nobody scans; what matters when triaging a
 * mailbox of 150 a day is whether the mail came today, yesterday or before. Same as
 * `formatDate` in `shared/utils/format`: a value that does not parse is shown as it came,
 * because a `RangeError` inside render would blank the whole screen.
 *
 * Local time on purpose: the day boundary is the one on the wall of the shop, not UTC's.
 */
const timeFormat = new Intl.DateTimeFormat('es-ES', { hour: '2-digit', minute: '2-digit' });
const dayFormat = new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'short' });
const dayYearFormat = new Intl.DateTimeFormat('es-ES', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** «hoy 23:07», «ayer 16:31», «11 sept», «11 sept 2025»; the raw string when it does not parse. */
export function formatWhen(value: string, now: Date = new Date()): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  if (sameDay(date, now)) return `hoy ${timeFormat.format(date)}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(date, yesterday)) return `ayer ${timeFormat.format(date)}`;
  return date.getFullYear() === now.getFullYear()
    ? dayFormat.format(date)
    : dayYearFormat.format(date);
}

/**
 * How long ago, in the coarsest unit that still says something: «hace un momento»,
 * «hace 3 min», «hace 2 h», «hace 4 días». For the line of state above the inbox
 * («Último correo: hace 3 min»). Empty when the value does not parse: a line of state
 * that says «Último correo: fecha rota» is worse than one that says nothing.
 */
export function formatAgo(value: string, now: Date = new Date()): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const seconds = Math.max(0, Math.round((now.getTime() - date.getTime()) / 1000));
  if (seconds < 60) return 'hace un momento';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'hace 1 día' : `hace ${days} días`;
}

/**
 * A day the way Victor writes it in a sentence: «29/09», or «29/09/2025» when it is not
 * this year, so an old lesson of the sender memory is not read as a recent one. Same wall
 * clock as the rest of this file (local time on purpose). Empty when the value does not
 * parse: a sentence that says «la fecha rota» is worse than one without the date.
 *
 * Written out by hand and not with `Intl`: `es-ES` with `2-digit` gives «29/9» in some
 * ICU builds and «29/09» in others, and a date that changes with the runtime cannot be
 * pinned by a test.
 */
export function formatDayMonth(value: string, now: Date = new Date()): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const day = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
  return date.getFullYear() === now.getFullYear() ? day : `${day}/${date.getFullYear()}`;
}
